// ManualPaymentForm.tsx
//
// Sprint 134 (R12-1, ADR-129) — the manager's "record a payment received offline"
// form (cheque / cash / bank transfer).
//
// Round 12 found this form collected *Select Owner* and *Payment Method* as
// required fields and then threw both away: `handleProcessPayment` sent only
// `feeId` to `markFeeAsPaidApi`, so choosing "Olivia / Cheque" recorded
// "Liam / MANUAL". It also shipped as TWO byte-identical copies (a tab body and a
// quick-access modal) wired to one handler — so every fix had to be made twice, and
// the tab copy had no `Form` instance at all, meaning its `resetFields()` was a
// silent no-op. This component is the single copy; both surfaces render it.
//
// Three deliberate choices:
//
//  1. **The owner select emits the canonical login-user UUID** (`userId`), not the
//     numeric `Owner.id`. `Fee.ownerId`/`Payment.ownerId` are the user UUID (V31 /
//     Sprint 107), so the numeric id could never match and the server could not
//     have validated our choice. (Sprint 136 canonicalises this repo-wide; this
//     form needs it now to work at all.)
//  2. **Fees are filtered to the selected owner**, and the fee clears when the
//     owner changes. Previously the picker offered every owner's fees regardless
//     of who was selected, so the natural click path recorded against the wrong
//     person. The mismatch is *also* rejected server-side — this just stops the
//     operator reaching a state the server must refuse.
//  3. **There is no Amount field.** `mark-paid` settles the fee in full and the
//     server derives the amount from the fee (base + tax); a free-text amount was
//     collected and discarded, promising partial payments that do not exist.
//     The full settled amount is shown, read-only, once a fee is chosen.

import React, { useMemo } from 'react';
import { Alert, Button, Form, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { Fee, Owner, SettlementMethod } from '../../types';

const { Option } = Select;
const { Text } = Typography;

export interface ManualPaymentValues {
  ownerId: string;
  feeId: string;
  method: SettlementMethod;
}

interface ManualPaymentFormProps {
  form: FormInstance<ManualPaymentValues>;
  owners: Owner[];
  /** Unpaid fees for this condo (PENDING *and* OVERDUE — see note below). */
  settleableFees: Fee[];
  onSubmit: (values: ManualPaymentValues) => void | Promise<void>;
  submitting?: boolean;
  /** Rendered by the modal copy to add a Cancel button beside Record Payment. */
  extraActions?: React.ReactNode;
}

/** The offline methods a manager may record. Values are backend PaymentMethod
 *  enum names, sent verbatim (see SettlementMethod). */
const METHOD_OPTIONS: Array<{ value: SettlementMethod; label: string }> = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE',        label: 'Cheque' },
  { value: 'CASH',          label: 'Cash' },
];

export const ManualPaymentForm: React.FC<ManualPaymentFormProps> = ({
  form, owners, settleableFees, onSubmit, submitting, extraActions,
}) => {
  // Watch the owner so the fee list can narrow to them.
  const selectedOwnerId = Form.useWatch('ownerId', form);
  const selectedFeeId   = Form.useWatch('feeId', form);

  const feesForOwner = useMemo(
    () => (selectedOwnerId
      ? settleableFees.filter(f => f.ownerId === selectedOwnerId)
      : []),
    [settleableFees, selectedOwnerId],
  );

  const selectedFee = useMemo(
    () => settleableFees.find(f => f.id === selectedFeeId),
    [settleableFees, selectedFeeId],
  );

  // The server settles base + tax; show the same total so the operator sees the
  // figure that will actually land in the ledger.
  const settledTotal = selectedFee
    ? (selectedFee.amount ?? 0) + (selectedFee.taxAmount ?? 0)
    : null;

  return (
    <Form<ManualPaymentValues> form={form} layout="vertical" onFinish={onSubmit}>
      <Alert
        type="info"
        showIcon
        message="Manual payments are recorded for cash, cheque, or bank transfers received outside the system."
        style={{ marginBottom: 20, borderRadius: 8 }}
      />

      <Form.Item
        name="ownerId"
        label="Select Owner"
        rules={[{ required: true, message: 'Choose the owner who paid' }]}
      >
        <Select
          placeholder="Choose owner"
          showSearch
          optionFilterProp="children"
          // Changing the owner invalidates the fee chosen under the previous one.
          onChange={() => form.setFieldsValue({ feeId: undefined as unknown as string })}
        >
          {owners.map((o: Owner) => (
            <Option key={o.id} value={o.userId ?? o.id}>
              {o.name} (Unit {o.unitNumber})
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="feeId"
        label="Apply to Fee"
        rules={[{ required: true, message: 'Choose the fee this payment settles' }]}
        extra={
          selectedOwnerId && feesForOwner.length === 0
            ? 'This owner has no outstanding fees.'
            : undefined
        }
      >
        <Select placeholder={selectedOwnerId ? 'Choose fee' : 'Select an owner first'}
                disabled={!selectedOwnerId}>
          {feesForOwner.map((f: Fee) => (
            <Option key={f.id} value={f.id}>
              {f.description} — ${f.amount}{f.status === 'OVERDUE' ? ' (overdue)' : ''}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="method"
        label="Payment Method"
        rules={[{ required: true, message: 'Choose how the payment was received' }]}
      >
        <Select placeholder="Choose method">
          {METHOD_OPTIONS.map(m => (
            <Option key={m.value} value={m.value}>{m.label}</Option>
          ))}
        </Select>
      </Form.Item>

      {settledTotal !== null && (
        <Form.Item label="Amount">
          <Text strong data-testid="manual-payment-amount">
            ${settledTotal.toFixed(2)}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              The fee is settled in full. Partial payments aren&apos;t supported yet.
            </Text>
          </div>
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        {extraActions ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            {extraActions}
            <Button type="primary" htmlType="submit" loading={submitting}>
              Record Payment
            </Button>
          </div>
        ) : (
          <Button type="primary" htmlType="submit" block size="large"
                  loading={submitting} style={{ borderRadius: 8 }}>
            Record Payment
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

export default ManualPaymentForm;
